import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import { appContext } from '../../../../AppContext';
import { INode } from './DataModels';
import { JSX } from 'react';
import { IRouterProps, withRouting } from "@iconics/components";
import { Button, ButtonBar, Card, DeleteButton, GridContainer, GridItem, IPageNavSection, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons, TextField, TimeZonePicker } from "@iconics/components";
import { SerendieSymbol } from '@serendie/symbols';
import { Switch } from '@serendie/ui';
import { MultilingualTextField } from "@iconics/components";
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import OntologyNodesCard, { IOntologyNode } from './OntologyNodesCard';
import DataPointsCard from './DataPointsCard';
import OntologyNodeParametersCard from './OntologyNodeParametersCard';
import { Validator } from '@iconics/core';
import { TypeHelper } from '@iconics/core';
import { QueryParamsHelper } from '../../../../Common/QueryParamsHelper';
import { QueryParams } from './QueryParams';

class EditNode extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);
    private get labels() { return appContext().labels; }
    private get local() { return appContext().localStorageProvider; }
    private userCanUpdate: boolean;
    private userCanDelete: boolean;
    private languageValuesHelper: LanguageValuesHelper;
    private queryParams = new QueryParamsHelper(new QueryParams(), firstLoad => this.handleQueryParamsUpdated(firstLoad), this.props.navigate);

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

        this.state =
        {
            name: '',
            nameError: '',
            displayName: this.languageValuesHelper.default(),
            displayNameError: '',
            isEnabled: false,
            sortOrder: '',
            timeZoneInherit: true,
            timeZone: '',
            timeZoneError: '',
            timeZoneDST: false,
            nodeId: 0,
            nodeParentId: undefined,
            ontologyNodes: [],
            sections: [],
            concurrencyStamp: ''
        };

        this.userCanUpdate = this.local.hasRight('DATAMODEL.Nodes.Update');
        this.userCanDelete = this.local.hasRight('DATAMODEL.Nodes.Delete');
    }

    private get nodeId(): number
    {
        return Number.parseInt(this.props.params.id ?? '0');
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.Platform_Node_PageTitle;
        this.backUrl = `/admin/nodes/tree-view?expanded-node-id=${this.props.params.id}`;
        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();

        if (!this.props.new)
        {
            await this.loading(() => this.loadNode());
        }
        else
        {
            this.setState({ nodeParentId: this.nodeId });
        }
        this.queryParams.evaluate();
    }

    public async handleQueryParamsUpdated(_: boolean)
    {
        const { source, root } = this.queryParams.value;
        if (source && source === 'graph' && root)
        {
            this.backUrl = `/admin/nodes/graph-view/${root}`;
        }
        else
        {
            this.backUrl = `/admin/nodes/tree-view?expanded-node-id=${this.props.params.id}`;
        }
    }

    public async componentDidUpdate(prevProps: Readonly<IProps>): Promise<void>
    {
        this.queryParams.evaluate(this.props.location, prevProps.location);
        if (prevProps.new && !this.props.new)
        {
            await this.loading(() => this.loadNode());
        }
    }

    private async loadNode(): Promise<void>
    {
        const nodeData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.get<INode>({
            nodeId: this.nodeId,
            tenancy: appContext().tenantId,
        });

        
        const timeZoneInherit = nodeData.TimeZoneType !== 1;

        await this.setStateAsync({
            name: nodeData.Name,
            displayName: this.languageValuesHelper.fromString(nodeData.DisplayName),
            isEnabled: TypeHelper.toBoolean(nodeData.IsEnabled) ?? false,
            sortOrder: nodeData.SortOrder,
            timeZoneInherit: timeZoneInherit,
            timeZone: nodeData.TimeZone ?? '',
            timeZoneDST: TypeHelper.toBoolean(nodeData.TimeZoneDST) ?? false,
            nodeId: nodeData.Id,
            nodeParentId: nodeData.Parent_ID,
            concurrencyStamp: nodeData["@odata.etag"]
        });

        this.handleTimeZoneInheritChange(timeZoneInherit);
    }
    
    private handleTimeZoneInheritChange(value: boolean): void 
    {
        if (value)
        {
            this.validator.removeInput('timeZone');
            this.setState({ timeZoneInherit: value, timeZone: '', timeZoneError: '' });
        }
        else
        {
            this.validator
                .addInput('timeZone', () => this.state.timeZone, i => this.setState({ timeZoneError: i }))
                .isRequired();
            this.setState({ timeZoneInherit: value });
        }
    }

    private async handleDelete(): Promise<void>
    {
        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.delete({
                tenancy: appContext().tenantId,
                nodeId: this.state.nodeId,
            });
            this.props.navigate(`/admin/nodes/tree-view?expanded-node-id=${this.state.nodeParentId}`)
        });
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        await this.loading(async () =>
        {
            if (!this.props.new)
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.byNodeid.patch({
                    tenancy: appContext().tenantId,
                    nodeId: this.state.nodeId,
                    body: {
                        Name: this.state.name,
                        DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                        IsEnabled: this.state.isEnabled ? 1 : 0,
                        SortOrder: this.state.sortOrder,
                        TimeZoneType: this.state.timeZoneInherit ? 'Inherit' : 'Override',
                        TimeZone: this.state.timeZone,
                        TimeZoneDST: this.state.timeZoneDST ? 1 : 0,
                        "@odata.etag": this.state.concurrencyStamp
                    }
                });
                this.props.navigate(this.backUrl);
            }
            else
            {
                let parentId: number | null;
                if (this.props.params.id)
                {
                    parentId = this.nodeId;
                }
                else
                {
                    parentId = null;
                }
                const response = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.post<INode>({
                    tenancy: appContext().tenantId,
                    body: {
                        Name: this.state.name,
                        DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                        Parent_ID: parentId,
                        IsEnabled: this.state.isEnabled ? 1 : 0,
                        SortOrder: this.state.sortOrder,
                        TimeZoneType: this.state.timeZoneInherit ? 'Inherit' : 'Override',
                        TimeZone: this.state.timeZone,
                        TimeZoneDST: this.state.timeZoneDST ? 1 : 0
                    }
                });
                this.props.navigate(`/admin/nodes/${response.Id}/edit`, { replace: true });
            }
        });
    }

    public render(): JSX.Element
    {
        return (
            <LayoutContainer>
                <LayoutContent>
                    <GridContainer spacing="cards">
                        <GridItem>
                            <Card
                                title={this.labels.Platform_Node_DetailsTitle}
                                subTitle={this.labels.Platform_Node_DetailsSubTitle}
                                onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
                                icon={<SerendieSymbol name='layer' />}
                            >
                                <GridContainer>
                                    <GridItem expanded={6}>
                                        <TextField
                                            required
                                            label={this.labels.Node_Name}
                                            placeholder={this.labels.Node_Name}
                                            value={this.state.name}
                                            onChange={e => this.setState({ name: e.target.value }, () => this.validator.validate('name'))}
                                            invalid={!!this.state.nameError}
                                            invalidMessage={this.state.nameError}
                                        />
                                    </GridItem>
                                    <GridItem expanded={6}>
                                        <MultilingualTextField
                                            label={this.labels.Node_DisplayName}
                                            placeholder={this.labels.Node_DisplayName}
                                            value={this.state.displayName}
                                            onChange={value => this.setState({ displayName: value })}
                                            invalid={!!this.state.displayNameError}
                                            invalidMessage={this.state.displayNameError}
                                            localisation={appContext().localisation}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <Switch
                                            label={this.labels.Node_IsEnabled}
                                            checked={this.state.isEnabled}
                                            onCheckedChange={e => this.setState({ isEnabled: e.checked as boolean })}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <TextField
                                            label={this.labels.Node_SortOrder}
                                            placeholder={this.labels.Node_SortOrder}
                                            value={this.state.sortOrder}
                                            onChange={e => this.setState({ sortOrder: e.target.value })}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <Switch
                                            label={this.labels.Node_TimeZoneType}
                                            checked={this.state.timeZoneInherit}
                                            onCheckedChange={(e) => this.handleTimeZoneInheritChange(e.checked)}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <TimeZonePicker
                                            disabled={this.state.timeZoneInherit}
                                            label={this.labels.Nodes_TimeZone}
                                            placeholder={this.labels.Nodes_TimeZone}
                                            value={this.state.timeZone}
                                            onChange={tz => this.setState({ timeZone: tz }, () => this.validator.validate('timeZone'))}
                                            required={!this.state.timeZoneInherit}
                                            invalid={!!this.state.timeZoneError}
                                            invalidMessage={this.state.timeZoneError}
                                        />
                                    </GridItem>
                                    <GridItem>
                                        <Switch
                                            disabled={this.state.timeZoneInherit}
                                            label={this.labels.Node_TimeZoneDST}
                                            checked={this.state.timeZoneDST}
                                            onChange={() => this.setState({ timeZoneDST: !this.state.timeZoneDST })}
                                        />
                                    </GridItem>
                                    {!this.props.new &&
                                        <GridItem expanded={6}>
                                            <TextField
                                                disabled
                                                label={this.labels.Node_Id}
                                                placeholder={this.labels.Node_Id}
                                                value={this.state.nodeId}
                                            />
                                        </GridItem>
                                    }
                                    <GridItem expanded={6}>
                                        <TextField
                                            disabled
                                            label={this.labels.Node_Parent_Id}
                                            placeholder={this.labels.Node_Parent_Id}
                                            value={this.state.nodeParentId}
                                        />
                                    </GridItem>
                                </GridContainer>
                            </Card>
                        </GridItem>
                        {
                            !this.props.new &&
                            <GridItem>
                                <OntologyNodesCard
                                    nodeId={this.nodeId}
                                    onOntologyNodesLoaded={i => this.setState({ ontologyNodes: i })}
                                    onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 1) }))}
                                />
                            </GridItem>
                        }
                        {
                            !this.props.new &&
                            <GridItem>
                                <OntologyNodeParametersCard
                                    nodeId={this.nodeId}
                                    ontologyNodes={this.state.ontologyNodes}
                                    onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 2) }))}
                                />
                            </GridItem>
                        }
                        {
                            !this.props.new &&
                            <GridItem>
                                <DataPointsCard
                                    nodeId={this.nodeId}
                                    onAdd={() => this.props.navigate(`/admin/nodes/${this.nodeId}/data-points/new`)}
                                    onEdit={i => this.props.navigate(`/admin/nodes/${this.nodeId}/data-points/${i}/edit`)}
                                    onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 3) }))}
                                />
                            </GridItem>
                        }
                    </GridContainer>
                </LayoutContent>
                <LayoutRight>
                    <PageNav
                        sections={this.state.sections}
                    />
                </LayoutRight>
                <LayoutFooter>
                    <ButtonBar>
                        <LeftButtons>
                            {
                                !this.props.new &&
                                <DeleteButton
                                    disabled={!this.userCanDelete}
                                    dialogMessage={this.labels.Platform_Node_ConfirmDeleteMsg}
                                    onDelete={() => this.handleDelete()}
                                />
                            }
                        </LeftButtons>
                        <RightButtons>
                            <Button
                                styleType='outlined'
                                onClick={() => this.props.navigate(this.backUrl)}
                            >
                                {this.labels.Platform_CommonAction_Cancel}
                            </Button>
                            <Button
                                styleType='filled'
                                disabled={!this.userCanUpdate || !this.validator.isValid}
                                onClick={() => this.handleSave()}
                            >
                                {this.labels.Platform_CommonAction_Save}
                            </Button>
                        </RightButtons>
                    </ButtonBar>
                </LayoutFooter>
            </LayoutContainer>
        )
    }
}

export default withRouting(EditNode);

export interface IProps extends IRouterProps<IQueryParams>
{
    new: boolean;
}

export interface IState
{
    name: string;
    nameError: string;
    displayName: ILanguageValues;
    displayNameError: string;
    isEnabled: boolean;
    sortOrder: string;
    timeZoneInherit: boolean;
    timeZone: string;
    timeZoneError: string;
    timeZoneDST: boolean;
    nodeId: number;
    nodeParentId?: number
    ontologyNodes: IOntologyNode[];
    sections: IPageNavSection[];
    concurrencyStamp: string;
}

export interface IQueryParams
{
    id: string | undefined;
    parentId: string | undefined;
}

export type FieldName = 'Name' | 'TimeZoneType' | 'TimeZoneDST';
